/* Common Imports */

import React from "react";
import { styled, useTheme } from "@mui/system";

/* MUI Imports */

import { Dialog, IconButton, LinearProgress, Slide, SwipeableDrawer, Typography, useMediaQuery } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Close } from "@mui/icons-material";

/* Redux Imports */

import StateInterface from "../../../redux-magic/state-interface";
import { createRequirementThunk } from "../../../redux-magic/store";

/* Component Imports */

import BeegruButton from "../../common-components/buttons/BeegruButton";
import PropertyRequirementStep from "./steps/PropertyRequirementStep";
import AdditionalDetailsStep from "./steps/AdditionalDetailsStep";
import ReviewStep from "./steps/ReviewStep";
import SubmissionSuccessModal from "./SubmissionSuccessModal";

/* Helper Imports */

import { brokerageOnTransactionChange, isBrokerageValueValid } from "./brokerage-config";

/* Styled Components */

const ModalHeader = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 1.5rem",
	borderBottom: "1px solid #e0e0e0",
}));

const ModalContent = styled("div")(({ theme }) => ({
	padding: "1.5rem",
	overflowY: "auto",
	flex: 1,
	[theme.breakpoints.down("sm")]: {
		padding: "1rem",
	},
}));

const ModalFooter = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.5rem",
	padding: "0.75rem 1.5rem",
	borderTop: "1px solid #e0e0e0",
}));

const ProgressContainer = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.5rem",
	width: "100%",
}));

const ProgressSegment = styled(LinearProgress)<{ active?: boolean }>(({ active }) => ({
	flex: 1,
	height: "4px",
	borderRadius: "2px",
	backgroundColor: "#e0e0e0",
	"& .MuiLinearProgress-bar": {
		backgroundColor: active ? "#E07934" : "#e0e0e0",
	},
}));

const FooterActions = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	gap: "1rem",
	width: "100%",
}));

const DrawerContainer = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	height: "90vh",
	borderTopLeftRadius: "1rem",
	borderTopRightRadius: "1rem",
	overflow: "hidden",
}));

const DrawerHandle = styled("div")(() => ({
	width: "2.5rem",
	height: "0.25rem",
	backgroundColor: "#bdbdbd",
	borderRadius: "0.125rem",
	margin: "0.5rem auto",
}));

/* Dialog Transition */

const DialogTransition = React.forwardRef(function Transition(
	props: TransitionProps & { children: React.ReactElement },
	ref: React.Ref<unknown>,
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

/* Component */

const CreateRequirementModal = ({
	open,
	handleClose,
	dispatch,
	session,
	size = "default",
	draft,
	onDraftSaved,
}: {
	open: boolean;
	handleClose: () => void;
	dispatch: Function;
	session: StateInterface["redux_session"];
	/* "small" = narrower desktop dialog that fits its content; mobile drawer is unaffected */
	size?: "default" | "small";
	/** A saved draft to reopen (My Requirements' Continue Editing, the app's draft resume): the form starts from it,
	    Save & Exit updates it and Submit sends it for review, instead of creating a new requirement. */
	draft?: any;
	/** Called after a draft was saved or submitted, so the list can reload. */
	onDraftSaved?: () => void;
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md1190"));

	/* Step state */

	const [activeStep, setActiveStep] = React.useState(0);
	const [showSuccess, setShowSuccess] = React.useState(false);
	const [submitting, setSubmitting] = React.useState(false);
	/* Shown above the footer when posting or saving the draft failed; the form stays as it was */
	const [submitError, setSubmitError] = React.useState("");

	/* Form data state */

	const [formData, setFormData] = React.useState({
		transaction_type: "",
		property_category: "",
		property_type: [] as Array<string>,
		locations: [] as Array<any>,
		budget: { min: 0, max: 0, currency: "INR", is_flexible: false },
		configuration: { bhk: [] as number[], area_min: 0, area_max: 0, area_unit: "sqft", furnishing: "", possession_by: "", seats: 0, plot_dimensions: [] as string[] },
		notes: "",
		brokerage: { is_offering_brokerage: false, value: 0 },
		contact_preference: [] as Array<string>,
	});

	/* Reopening a draft: the form starts from what was saved (the same keys the create payload sends). */

	React.useEffect(() => {
		if (!open || !draft) return;
		setActiveStep(0);
		setFormData({
			transaction_type: draft.transaction_type || "",
			property_category: draft.property_category || "",
			property_type: Array.isArray(draft.property_type) ? draft.property_type : draft.property_type ? [draft.property_type] : [],
			locations: Array.isArray(draft.locations) ? draft.locations : [],
			budget: {
				min: draft.budget?.min || 0,
				max: draft.budget?.max || 0,
				currency: draft.budget?.currency || "INR",
				is_flexible: draft.budget?.is_flexible || false,
			},
			configuration: {
				bhk: draft.configuration?.bhk || [],
				area_min: draft.configuration?.area_min || 0,
				area_max: draft.configuration?.area_max || 0,
				area_unit: draft.configuration?.area_unit || "sqft",
				furnishing: draft.configuration?.furnishing || "",
				possession_by: draft.configuration?.possession_by || "",
				seats: draft.configuration?.seats || 0,
				plot_dimensions: draft.configuration?.plot_dimensions || [],
			},
			notes: draft.notes || "",
			brokerage: {
				is_offering_brokerage: draft.brokerage?.is_offering_brokerage || false,
				value: draft.brokerage?.value || 0,
			},
			contact_preference: draft.contact_preference || [],
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, draft?._id]);

	/* A draft is saved through update-requirement (draft or in review only), and Submit then moves it draft -> in review
	   (update-requirement-status's one transition from a draft), the app's save_as_draft: false on its update. */

	const saveDraft = async (submit: boolean): Promise<boolean> => {
		const post = async (route: string, payload: any): Promise<any> =>
			(
				await fetch(process.env.PRODUCTION_API_URL + "requirements/" + route, {
					method: "POST",
					cache: "no-store",
					headers: { "Content-Type": "text/plain" },
					body: JSON.stringify(payload),
				})
			).json();
		try {
			const requirementId: string = String(draft._id);
			const updated = await post("update-requirement", { requirement_id: requirementId, ...formData });
			if (updated?.status !== "success") return false;
			// One already in review (Edit while in review, 3 Nov 2026) stays in review: only its details change.
			if (submit && String(draft.status || "") !== "in_review") {
				const submitted = await post("update-requirement-status", { requirement_id: requirementId, new_status: "in_review" });
				if (submitted?.status !== "success") return false;
			}
			onDraftSaved?.();
			return true;
		} catch {
			return false;
		}
	};

	/* Update form data helper */

	const updateFormData = (field: string, value: any) => {
		setFormData((prev) => {
			/* Rent brokerage is days and sale brokerage a percentage, so a switch between them resets the value */
			if (field === "transaction_type") {
				return { ...prev, transaction_type: value, brokerage: brokerageOnTransactionChange(prev.brokerage, prev.transaction_type, value) };
			}
			if (field.includes(".")) {
				const [parent, child] = field.split(".");
				return {
					...prev,
					[parent]: {
						...(prev as any)[parent],
						[child]: value,
					},
				};
			}
			return { ...prev, [field]: value };
		});
	};

	/* Navigation */

	const handleNext = () => {
		if (activeStep < 2) {
			setActiveStep((prev) => prev + 1);
		}
	};

	const handleBack = () => {
		if (activeStep > 0) {
			setActiveStep((prev) => prev - 1);
		}
	};

	/* Submit and Save & Exit: both wait for the server, and move on only when it says success.
	Before, the success screen showed (and Save & Exit closed) even when the post failed. */

	const postRequirement = async (saveAsDraft: boolean): Promise<boolean> => {
		if (draft) return saveDraft(!saveAsDraft);
		const payload = {
			...formData,
			posted_by: {
				user_id: session?.user?.dbUser?._id || "",
				business_profile_id: session?.user?.dbUser?.profile_context?.business_profile_id || "",
				role: session?.user?.dbUser?.profile_context?.is_business_profile
					? session?.user?.dbUser?.profile_context?.page_type || "agent"
					: "seeker",
				is_business_profile: session?.user?.dbUser?.profile_context?.is_business_profile || false,
				is_staff_post: false,
			},
			save_as_draft: saveAsDraft,
		};
		try {
			const response = await dispatch(createRequirementThunk(payload));
			return response?.status === "success";
		} catch {
			return false;
		}
	};

	const handleSubmit = async () => {
		if (submitting) return;
		setSubmitting(true);
		setSubmitError("");
		const posted = await postRequirement(false);
		setSubmitting(false);
		if (posted) {
			setShowSuccess(true);
		} else {
			setSubmitError("Something went wrong. Please try again");
		}
	};

	/* Save as draft */

	const handleSaveAndExit = async () => {
		if (submitting) return;
		setSubmitting(true);
		setSubmitError("");
		const saved = await postRequirement(true);
		setSubmitting(false);
		if (saved) {
			handleCloseAndReset();
		} else {
			setSubmitError("Couldn't save your draft. Please try again");
		}
	};

	/* Reset and close */

	const handleCloseAndReset = () => {
		setActiveStep(0);
		setShowSuccess(false);
		setSubmitting(false);
		setSubmitError("");
		setFormData({
			transaction_type: "",
			property_category: "",
			property_type: [],
			locations: [],
			budget: { min: 0, max: 0, currency: "INR", is_flexible: false },
			configuration: { bhk: [] as number[], area_min: 0, area_max: 0, area_unit: "sqft", furnishing: "", possession_by: "", seats: 0, plot_dimensions: [] as string[] },
			notes: "",
			brokerage: { is_offering_brokerage: false, value: 0 },
			contact_preference: [],
		});
		handleClose();
	};

	/* Success modal done */

	const handleSuccessDone = () => {
		handleCloseAndReset();
	};

	/* Validation — check if mandatory fields are filled for current step */

	const isStepValid = (): boolean => {
		switch (activeStep) {
			case 0:
				if (!formData.transaction_type) return false;
				if (!formData.property_category) return false;
				if (formData.locations.length === 0) return false;
				if (!formData.budget.max && !formData.budget.min) return false;
				return true;
			case 1:
				if (formData.brokerage.is_offering_brokerage && !isBrokerageValueValid(formData.brokerage.value, formData.transaction_type)) return false;
				return true;
			case 2:
				return true;
			default:
				return true;
		}
	};

	/* Step content */

	const renderStepContent = () => {
		switch (activeStep) {
			case 0:
				return <PropertyRequirementStep formData={formData} updateFormData={updateFormData} />;
			case 1:
				return <AdditionalDetailsStep formData={formData} updateFormData={updateFormData} />;
			case 2:
				return (
					<ReviewStep
						formData={formData}
						onEditStep={(step: number) => setActiveStep(step)}
					/>
				);
			default:
				return null;
		}
	};

	/* Step titles */

	const stepTitles = ["Tell us about your property requirement", "Add more details", "Review your requirement"];

	/* Modal body content */

	const modalBody = (
		<>
			{/* Header */}

			<ModalHeader>
				<Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
					{showSuccess ? "" : stepTitles[activeStep]}
				</Typography>
				{showSuccess ? null : (
					<BeegruButton
						variant="text"
						flavor="brown"
						size="small"
						onClick={handleSaveAndExit}
						disabled={submitting}
					>
						Save & Exit
					</BeegruButton>
				)}
			</ModalHeader>

			{/* Content */}

			{showSuccess ? (
				<SubmissionSuccessModal formData={formData} onDone={handleSuccessDone} />
			) : (
				<>
					<ModalContent>{renderStepContent()}</ModalContent>

					{/* Footer */}

					<ModalFooter>
						{submitError ? (
							<Typography role="alert" sx={{ fontSize: "0.813rem", color: theme.palette.error.main, marginBottom: "0.5rem" }}>
								{submitError}
							</Typography>
						) : null}
						<ProgressContainer>
							{[0, 1, 2].map((step) => (
								<ProgressSegment
									key={step}
									variant="determinate"
									value={activeStep >= step ? 100 : 0}
									active={activeStep >= step}
								/>
							))}
						</ProgressContainer>
						<FooterActions>
							{activeStep > 0 && (
								<BeegruButton variant="text" flavor="brown" onClick={handleBack}>
									Back
								</BeegruButton>
							)}
							{activeStep === 2 ? (
								<BeegruButton variant="contained" flavor="primary" onClick={handleSubmit} disabled={submitting}>
									Submit Requirement
								</BeegruButton>
							) : (
								<BeegruButton variant="contained" flavor="primary" onClick={handleNext} disabled={!isStepValid()}>
									Continue
								</BeegruButton>
							)}
						</FooterActions>
					</ModalFooter>
				</>
			)}
		</>
	);

	/* Render — Dialog on desktop, SwipeableDrawer on mobile */

	if (isMobile) {
		return (
			<SwipeableDrawer
				anchor="bottom"
				open={open}
				onClose={handleCloseAndReset}
				onOpen={() => {}}
				disableSwipeToOpen
				PaperProps={{
					sx: {
						height: "90vh",
						borderTopLeftRadius: "1rem",
						borderTopRightRadius: "1rem",
					},
				}}
			>
				<DrawerContainer>
					<DrawerHandle />
					{modalBody}
				</DrawerContainer>
			</SwipeableDrawer>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={handleCloseAndReset}
			maxWidth={size === "small" ? "sm" : "lg"}
			fullWidth
			TransitionComponent={DialogTransition}
			PaperProps={{
				sx: {
					borderRadius: "1rem",
					maxHeight: "90vh",
					minHeight: size === "small" ? "auto" : "70vh",
					display: "flex",
					flexDirection: "column",
				},
			}}
		>
			{modalBody}
		</Dialog>
	);
};

export default CreateRequirementModal;
